import { ref, get, update, onValue } from 'firebase/database';
import { database } from '../firebaseConfig';
import {
  PlayerRating,
  RatingHistoryEntry,
  Tournament,
  TournamentMatch,
  TournamentMatchResult,
} from '../types';

// ─── Triple Freezer ELO ───────────────────────────────────────────────────────
//
// Chess.com-style rating, adapted for a luck-heavy dice game played as
// best-of-N tournament series:
//
//   Δ = K × G × W × P × (S − E)        applied symmetrically (zero-sum)
//
//   E  expected score        1 / (1 + 10^((opp − own) / 400))
//   K  adaptive K-factor     40 first 10 series · 24 until 30 · 16 after
//   G  margin of victory     sweeps move more points than deciders
//   W  stage weight          pool 1.0 · playoff 1.1 · semis 1.15 · final 1.25
//   P  performance modifier  dominant play (accuracy diff) pays slightly
//                            more, lucky scrapes slightly less — capped ±8%
//
// Anti-farming: ratings are ONLY updated from completed tournament matches
// (registration is admin-approved), never from offline/free play. Byes and
// matches without a real opponent never touch ratings.

export const START_ELO = 1000;
export const ELO_FLOOR = 100;
export const PLACEMENT_GAMES = 5; // unranked/"placements" until this many rated series

const kFactor = (gamesRated: number): number =>
  gamesRated < 10 ? 40 : gamesRated < 30 ? 24 : 16;

const marginMultiplier = (winnerWins: number, loserWins: number): number => {
  const margin = Math.max(1, winnerWins - loserWins);
  return Math.min(1.4, 1 + 0.18 * (margin - 1));
};

/** Stage weight from match position — finals are worth more, like football Elo */
const stageWeight = (match: TournamentMatch, tournament: Tournament): number => {
  if (match.phase !== 'playoff' || match.roundIndex === undefined) return 1.0;
  const rounds = Object.values(tournament.playoffRounds || {});
  if (rounds.length === 0) return 1.1;
  const maxRound = Math.max(...rounds.map((r) => r.roundIndex));
  if (match.roundIndex === maxRound) return 1.25;       // Final
  if (match.roundIndex === maxRound - 1) return 1.15;   // Semifinals
  return 1.1;
};

/** Dominance modifier from stored per-match accuracy stats. Capped, optional. */
const performanceModifier = (
  result: TournamentMatchResult,
  winnerUid: string,
  loserUid: string
): number => {
  const w = result.stats?.[winnerUid]?.accuracy;
  const l = result.stats?.[loserUid]?.accuracy;
  if (typeof w !== 'number' || typeof l !== 'number') return 1.0;
  const diff = Math.max(-0.08, Math.min(0.08, ((w - l) / 100) * 0.15));
  return 1 + diff;
};

export const expectedScore = (own: number, opponent: number): number =>
  1 / (1 + Math.pow(10, (opponent - own) / 400));

export interface RatingDelta {
  winnerDelta: number;
  loserDelta: number;
}

/** Pure rating math for one completed series — exported for the replay tool */
export const computeRatingDelta = (
  winner: PlayerRating,
  loser: PlayerRating,
  match: TournamentMatch,
  result: TournamentMatchResult,
  tournament: Tournament
): RatingDelta => {
  const winnerWins = result.winner === match.player1.uid ? result.player1Wins : result.player2Wins;
  const loserWins = result.winner === match.player1.uid ? result.player2Wins : result.player1Wins;
  const loserUid = result.winner === match.player1.uid ? match.player2!.uid : match.player1.uid;

  const e = expectedScore(winner.elo, loser.elo);
  const g = marginMultiplier(winnerWins, loserWins);
  const w = stageWeight(match, tournament);
  const p = performanceModifier(result, result.winner, loserUid);

  // Winner and loser may have different K (provisional players move faster)
  const winnerDelta = Math.max(1, Math.round(kFactor(winner.gamesRated) * g * w * p * (1 - e)));
  const loserRaw = Math.max(1, Math.round(kFactor(loser.gamesRated) * g * w * p * (1 - e)));
  const loserDelta = -Math.min(loserRaw, loser.elo - ELO_FLOOR); // never below floor

  return { winnerDelta, loserDelta };
};

// ─── Tiers (ice-themed ladder) ────────────────────────────────────────────────

export interface RatingTier {
  key: string;
  name: string;
  emoji: string;
  min: number;       // inclusive lower bound
  color: string;
  glow: string;
}

export const TIERS: RatingTier[] = [
  { key: 'slush',      name: 'Slush',         emoji: '💧', min: -Infinity, color: '#9aa7b0', glow: 'rgba(154,167,176,0.35)' },
  { key: 'flurry',     name: 'Flurry',        emoji: '🌨️', min: 900,  color: '#7fd4ff', glow: 'rgba(127,212,255,0.35)' },
  { key: 'frost',      name: 'Frost',         emoji: '❄️', min: 1050, color: '#00d5ff', glow: 'rgba(0,213,255,0.45)' },
  { key: 'blizzard',   name: 'Blizzard',      emoji: '🌪️', min: 1200, color: '#4f8cff', glow: 'rgba(79,140,255,0.45)' },
  { key: 'permafrost', name: 'Permafrost',    emoji: '🧊', min: 1350, color: '#9b6bff', glow: 'rgba(155,107,255,0.45)' },
  { key: 'glacier',    name: 'Glacier',       emoji: '⛰️', min: 1500, color: '#ffd700', glow: 'rgba(255,215,0,0.45)' },
  { key: 'zero',       name: 'Absolute Zero', emoji: '👑', min: 1700, color: '#e8fbff', glow: 'rgba(232,251,255,0.6)' },
];

export const tierFor = (elo: number): RatingTier => {
  let tier = TIERS[0];
  for (const t of TIERS) if (elo >= t.min) tier = t;
  return tier;
};

export const isPlacement = (rating: PlayerRating | null | undefined): boolean =>
  !rating || (rating.gamesRated || 0) < PLACEMENT_GAMES;

export const freshRating = (): PlayerRating => ({
  elo: START_ELO,
  peak: START_ELO,
  gamesRated: 0,
  lastChange: 0,
  updatedAt: 0,
});

const normalizeRating = (raw: any): PlayerRating => ({
  elo: typeof raw?.elo === 'number' ? raw.elo : START_ELO,
  peak: typeof raw?.peak === 'number' ? raw.peak : (typeof raw?.elo === 'number' ? raw.elo : START_ELO),
  gamesRated: raw?.gamesRated || 0,
  lastChange: raw?.lastChange || 0,
  updatedAt: raw?.updatedAt || 0,
});

// ─── Reads ────────────────────────────────────────────────────────────────────

export const getRating = async (uid: string): Promise<PlayerRating> => {
  try {
    const snap = await get(ref(database, `users/${uid}/rating`));
    return snap.exists() ? normalizeRating(snap.val()) : freshRating();
  } catch {
    return freshRating();
  }
};

/** Batch-fetch ratings; missing/unreadable users come back as fresh (1000). */
export const getRatingsForUids = async (
  uids: string[]
): Promise<{ [uid: string]: PlayerRating }> => {
  const out: { [uid: string]: PlayerRating } = {};
  await Promise.all(
    uids.map(async (uid) => { out[uid] = await getRating(uid); })
  );
  return out;
};

export const subscribeRating = (
  uid: string,
  callback: (rating: PlayerRating) => void
) => {
  return onValue(ref(database, `users/${uid}/rating`), (snap) => {
    callback(snap.exists() ? normalizeRating(snap.val()) : freshRating());
  });
};

export const getRatingHistory = async (uid: string): Promise<RatingHistoryEntry[]> => {
  try {
    const snap = await get(ref(database, `users/${uid}/ratingHistory`));
    if (!snap.exists()) return [];
    return (Object.values(snap.val()) as RatingHistoryEntry[])
      .filter((e) => e && typeof e.at === 'number')
      .sort((a, b) => a.at - b.at);
  } catch {
    return [];
  }
};

// ─── Apply rating for one completed tournament match ─────────────────────────

const historyEntry = (
  matchKey: string,
  tournament: Tournament,
  match: TournamentMatch,
  opponent: { uid: string; name: string },
  opponentElo: number,
  before: number,
  change: number,
  won: boolean,
  stage: string | undefined,
  at: number
): RatingHistoryEntry => ({
  matchKey,
  tournamentId: tournament.id,
  tournamentName: tournament.name || 'Tournament',
  opponentUid: opponent.uid,
  opponentName: opponent.name || 'Unknown',
  opponentElo,
  before,
  after: before + change,
  change,
  won,
  phase: match.phase,
  stage: stage || undefined,
  at,
});

const stageLabel = (match: TournamentMatch, tournament: Tournament): string | undefined => {
  if (match.phase === 'pool') return tournament.groups?.[match.groupId || '']?.name;
  if (match.roundIndex !== undefined) {
    return tournament.playoffRounds?.[`${match.roundIndex}`]?.name;
  }
  return undefined;
};

/**
 * Update both players' ratings after a tournament match result is finalized.
 * Called from tournamentService AFTER the result write — never blocks or
 * alters match completion. Idempotent per match via the history key.
 */
export const applyMatchRating = async (
  tournament: Tournament,
  match: TournamentMatch,
  result: TournamentMatchResult,
  resultPath: string // RTDB path of the match node, for writing result/ratingChanges
): Promise<void> => {
  const p2 = match.player2;
  if (!p2) return; // byes never touch ratings

  const winnerUid = result.winner;
  const loserSide = winnerUid === match.player1.uid ? p2 : match.player1;
  const winnerSide = winnerUid === match.player1.uid ? match.player1 : p2;
  if (!winnerSide?.uid || !loserSide?.uid || winnerSide.uid === loserSide.uid) return;

  const matchKey = `${tournament.id}_${match.id}`;
  const safeKey = matchKey.replace(/[.#$/[\]]/g, '_');

  // Idempotency: if this match already wrote history for the winner, skip.
  const existing = await get(
    ref(database, `users/${winnerSide.uid}/ratingHistory/${safeKey}`)
  );
  if (existing.exists()) return;

  const [winnerRating, loserRating] = await Promise.all([
    getRating(winnerSide.uid),
    getRating(loserSide.uid),
  ]);

  const { winnerDelta, loserDelta } = computeRatingDelta(
    winnerRating, loserRating, match, result, tournament
  );

  const at = match.completedAt || Date.now();
  const stage = stageLabel(match, tournament);

  const updates: { [path: string]: any } = {};
  updates[`users/${winnerSide.uid}/rating`] = {
    elo: winnerRating.elo + winnerDelta,
    peak: Math.max(winnerRating.peak, winnerRating.elo + winnerDelta),
    gamesRated: winnerRating.gamesRated + 1,
    lastChange: winnerDelta,
    updatedAt: at,
  };
  updates[`users/${loserSide.uid}/rating`] = {
    elo: loserRating.elo + loserDelta,
    peak: loserRating.peak,
    gamesRated: loserRating.gamesRated + 1,
    lastChange: loserDelta,
    updatedAt: at,
  };
  updates[`users/${winnerSide.uid}/ratingHistory/${safeKey}`] = historyEntry(
    matchKey, tournament, match, loserSide, loserRating.elo,
    winnerRating.elo, winnerDelta, true, stage, at
  );
  updates[`users/${loserSide.uid}/ratingHistory/${safeKey}`] = historyEntry(
    matchKey, tournament, match, winnerSide, winnerRating.elo,
    loserRating.elo, loserDelta, false, stage, at
  );
  // Stamp the deltas onto the match result so screens can show "+12 ELO"
  updates[`${resultPath}/result/ratingChanges`] = {
    [winnerSide.uid]: winnerDelta,
    [loserSide.uid]: loserDelta,
  };

  await update(ref(database), updates);
};

// ─── Full replay (admin tool) ─────────────────────────────────────────────────

export interface ReplayOutcome {
  ratings: { [uid: string]: PlayerRating };
  history: { [uid: string]: { [key: string]: RatingHistoryEntry } };
  matchesRated: number;
  playersRated: number;
}

/**
 * Recompute every player's rating from scratch by replaying all completed
 * tournament matches in chronological order. Pure function — caller decides
 * whether to persist. Lets us bootstrap ratings from existing production
 * history and cleanly re-run after any formula change.
 */
export const replayAllRatings = (tournaments: Tournament[]): ReplayOutcome => {
  interface ReplayMatch {
    tournament: Tournament;
    match: TournamentMatch;
    at: number;
  }
  const all: ReplayMatch[] = [];

  tournaments.forEach((t) => {
    const collect = (m: TournamentMatch) => {
      if (m.status !== 'completed' || !m.result || !m.player2) return;
      // Walkovers are never rated. Live play skips ELO for them (see the
      // `!result.walkover` guards in tournamentService), because no game was
      // played and the stored score is synthetic or admin-entered. A replay
      // that rated them produced different ratings from the ones built during
      // the tournament — on real data, the admin "Recalculate ratings" button
      // changed 25 of 38 players' ratings (up to 36 points) from 8 walkovers.
      if (m.result.walkover) return;
      all.push({ tournament: t, match: m, at: m.completedAt || t.createdAt || 0 });
    };
    Object.values(t.poolMatches || {}).forEach(collect);
    Object.values(t.playoffRounds || {}).forEach((round) =>
      Object.values(round.matches || {}).forEach((m: any) => collect(m as TournamentMatch))
    );
  });

  all.sort((a, b) => a.at - b.at);

  const ratings: { [uid: string]: PlayerRating } = {};
  const history: { [uid: string]: { [key: string]: RatingHistoryEntry } } = {};
  const rated = new Set<string>();

  const ratingOf = (uid: string): PlayerRating =>
    ratings[uid] || (ratings[uid] = freshRating());

  all.forEach(({ tournament, match, at }) => {
    const result = match.result!;
    const p2 = match.player2!;
    const winnerSide = result.winner === match.player1.uid ? match.player1 : p2;
    const loserSide = result.winner === match.player1.uid ? p2 : match.player1;
    if (!winnerSide?.uid || !loserSide?.uid || winnerSide.uid === loserSide.uid) return;

    const matchKey = `${tournament.id}_${match.id}`;
    const safeKey = matchKey.replace(/[.#$/[\]]/g, '_');
    if (rated.has(safeKey)) return;
    rated.add(safeKey);

    const w = ratingOf(winnerSide.uid);
    const l = ratingOf(loserSide.uid);
    const { winnerDelta, loserDelta } = computeRatingDelta(w, l, match, result, tournament);
    const stage = stageLabel(match, tournament);

    (history[winnerSide.uid] = history[winnerSide.uid] || {})[safeKey] = historyEntry(
      matchKey, tournament, match, loserSide, l.elo, w.elo, winnerDelta, true, stage, at
    );
    (history[loserSide.uid] = history[loserSide.uid] || {})[safeKey] = historyEntry(
      matchKey, tournament, match, winnerSide, w.elo, l.elo, loserDelta, false, stage, at
    );

    w.elo += winnerDelta;
    w.peak = Math.max(w.peak, w.elo);
    w.gamesRated += 1;
    w.lastChange = winnerDelta;
    w.updatedAt = at;

    l.elo += loserDelta;
    l.gamesRated += 1;
    l.lastChange = loserDelta;
    l.updatedAt = at;
  });

  return {
    ratings,
    history,
    matchesRated: rated.size,
    playersRated: Object.keys(ratings).length,
  };
};

/** Persist a replay outcome — overwrites rating + ratingHistory for affected players */
export const persistReplayOutcome = async (outcome: ReplayOutcome): Promise<void> => {
  const updates: { [path: string]: any } = {};
  Object.entries(outcome.ratings).forEach(([uid, rating]) => {
    updates[`users/${uid}/rating`] = rating;
    updates[`users/${uid}/ratingHistory`] = outcome.history[uid] || null;
  });
  if (Object.keys(updates).length) await update(ref(database), updates);
};
